import { WorkflowExecutionId } from "@sp-crm/core";
import {
    AdvancedSearchResults,
    RenderRowActionParams,
} from "components/advanced-search/advanced-search-results";
import { DeleteButton } from "components/ui/action-button";
import {
    AdvancedSearchCondition,
    AdvancedSearchConditionNodeType,
    AdvancedSearchEntityType,
    AdvancedSearchRequest,
    AdvancedSearchRequestType,
    AdvancedSearchSelect,
    FieldConditionOperator,
    useCancelWorkflowExecutionMutation,
    WorkflowExecutionResult,
} from "generated/graphql";
import React, { useCallback, useMemo, useState } from "react";
import { useWorkflowEntityTypes } from "store/selectors/bridge";

const entityRelationConfig: Record<
    string,
    { relation: string; selectField: string; selectTitle: string }
> = {
    [AdvancedSearchEntityType.Client]: {
        relation: "client",
        selectField: "client.name",
        selectTitle: "Client",
    },
    [AdvancedSearchEntityType.HomeCareLead]: {
        relation: "homeCareLead",
        selectField: "homeCareLead.selfContactEntity.name",
        selectTitle: "Home Care Client",
    },
};

function buildRelationCondition(relation: string): AdvancedSearchCondition {
    return {
        nodeType: AdvancedSearchConditionNodeType.RelationCondition,
        operator: FieldConditionOperator.RelationMatches,
        fieldName: relation,
        children: [
            {
                nodeType: AdvancedSearchConditionNodeType.FieldCondition,
                operator: FieldConditionOperator.UserIsMe,
                fieldName: "assignedUserId",
            },
        ],
    };
}

function buildExecutionRequest(
    entityTypes: AdvancedSearchEntityType[],
): AdvancedSearchRequest {
    const configs = entityTypes.map(et => entityRelationConfig[et]).filter(Boolean);

    const relationConditions = configs.map(c => buildRelationCondition(c.relation));

    const ownerCondition: AdvancedSearchCondition =
        relationConditions.length === 1
            ? relationConditions[0]
            : {
                  nodeType: AdvancedSearchConditionNodeType.Or,
                  children: relationConditions,
              };

    const select: AdvancedSearchSelect[] = [
        { fieldName: "workflow.name", title: "Workflow" },
        ...configs.map(c => ({
            fieldName: c.selectField,
            title: c.selectTitle,
        })),
        { fieldName: "scheduledAt" },
    ];

    return {
        entityType: AdvancedSearchEntityType.WorkflowExecution,
        type: AdvancedSearchRequestType.Implicit,
        condition: {
            nodeType: AdvancedSearchConditionNodeType.And,
            children: [
                {
                    nodeType: AdvancedSearchConditionNodeType.FieldCondition,
                    operator: FieldConditionOperator.StringEquals,
                    fieldName: "result",
                    textValue: WorkflowExecutionResult.Pending,
                },
                ownerCondition,
            ],
        },
        select,
        sort: { column: "scheduledAt", ascending: true },
    };
}

export const WorkflowExecutionList: React.FC<unknown> = () => {
    const workflowEntityTypes = useWorkflowEntityTypes();

    const defaultRequest = useMemo(
        () => buildExecutionRequest(workflowEntityTypes),
        [workflowEntityTypes],
    );

    const [advancedSearchRequest, setAdvancedSearchRequest] =
        useState<AdvancedSearchRequest | null>(null);

    const request = advancedSearchRequest ?? defaultRequest;

    const cancelMutation = useCancelWorkflowExecutionMutation();
    const handleDelete = useCallback(
        async (executionId: WorkflowExecutionId, refetch: () => void) => {
            await cancelMutation.mutateAsync({ workflowExecutionId: executionId });
            refetch();
        },
        [cancelMutation],
    );

    const renderDeleteButton = useCallback(
        (params: RenderRowActionParams) => {
            return (
                <DeleteButton
                    backgroundColor="bg-white"
                    onClick={() =>
                        handleDelete(
                            params.record.id as WorkflowExecutionId,
                            params.refetch,
                        )
                    }
                    confirm={{
                        title: "Remove",
                        message:
                            "Are you sure you want to stop this workflow from running?",
                        confirmLabel: "Yes, stop this workflow",
                        cancelLabel: "No, keep it running",
                    }}
                />
            );
        },
        [handleDelete],
    );

    if (workflowEntityTypes.length === 0) {
        return null;
    }

    return (
        <AdvancedSearchResults
            request={request}
            onChangeRequest={setAdvancedSearchRequest}
            renderRowAction={renderDeleteButton}
            includeIconLinks={true}
        />
    );
};
